import { createAsyncThunk } from '@reduxjs/toolkit';
import { getSessionConversionStats } from '../../api/sessionConversion';
import store from '../index';
import { setNotifications } from '../slices/notifications';
import dayjs from 'dayjs';

export const getSessionConversionStatsThunk = createAsyncThunk(
    'sessionConversion/getSessionConversionStats',
    async (queryParams: any, { rejectWithValue, getState }) => {
        try {
            const { createdAt_min, createdAt_max } = queryParams;
            const startDate = dayjs(createdAt_min);
            const endDate = dayjs(createdAt_max);
            const daysDiff = endDate.diff(startDate, 'day');
            
            // Get cached days from state
            const state = getState() as any;
            const cachedDays = state.sessionConversion?.cachedDays || {};
            
            // If single day, fetch it
            if (daysDiff === 0) {
                const dateStr = startDate.format('YYYY-MM-DD');
                
                // Check if already cached
                if (cachedDays[dateStr]) {
                    const cached = cachedDays[dateStr];
                    return {
                        aggregate: {
                            sessionStats: [{
                                totalSessions: cached.totalSessions,
                                sessionsWithEvents: cached.sessionsWithEvents.toString(),
                                sessionsWithoutEvents: (cached.totalSessions - cached.sessionsWithEvents).toString(),
                                sessionsWithAddToCart: cached.sessionsWithAddToCart.toString(),
                                sessionsWithOrders: cached.sessionsWithOrders.toString(),
                                uniqueCustomers: 0 // Not used for calculated aggregate
                            }]
                        },
                        dailyStats: undefined,
                        newCachedDays: {}
                    };
                }
                
                const response = await getSessionConversionStats(queryParams);
                const dayData = {
                    totalSessions: response.sessionStats[0].totalSessions,
                    sessionsWithEvents: parseInt(response.sessionStats[0].sessionsWithEvents),
                    sessionsWithAddToCart: parseInt(response.sessionStats[0].sessionsWithAddToCart),
                    sessionsWithOrders: parseInt(response.sessionStats[0].sessionsWithOrders)
                };
                
                return {
                    aggregate: response,
                    dailyStats: undefined,
                    newCachedDays: { [dateStr]: dayData }
                };
            }
            
            // Multiple days - fetch only missing days
            const daysToFetch: string[] = [];
            const allDates: string[] = [];
            
            for (let i = 0; i <= daysDiff; i++) {
                const dateStr = startDate.add(i, 'day').format('YYYY-MM-DD');
                allDates.push(dateStr);
                if (!cachedDays[dateStr]) {
                    daysToFetch.push(dateStr);
                }
            }
            
            // Fetch missing days
            const newCachedDays: Record<string, any> = {};
            if (daysToFetch.length > 0) {
                const fetchPromises = daysToFetch.map(dateStr =>
                    getSessionConversionStats({
                        createdAt_min: dateStr,
                        createdAt_max: dateStr
                    }).then(response => ({
                        date: dateStr,
                        data: {
                            totalSessions: response.sessionStats[0].totalSessions,
                            sessionsWithEvents: parseInt(response.sessionStats[0].sessionsWithEvents),
                            sessionsWithAddToCart: parseInt(response.sessionStats[0].sessionsWithAddToCart),
                            sessionsWithOrders: parseInt(response.sessionStats[0].sessionsWithOrders)
                        }
                    }))
                );
                
                const fetchedData = await Promise.all(fetchPromises);
                fetchedData.forEach(item => {
                    newCachedDays[item.date] = item.data;
                });
            }
            
            // Combine cached and new data for all requested dates
            const combinedCache = { ...cachedDays, ...newCachedDays };
            const dailyStats = allDates.map(dateStr => ({
                date: dateStr,
                totalSessions: combinedCache[dateStr].totalSessions,
                sessionsWithEvents: combinedCache[dateStr].sessionsWithEvents,
                sessionsWithAddToCart: combinedCache[dateStr].sessionsWithAddToCart,
                sessionsWithOrders: combinedCache[dateStr].sessionsWithOrders
            }));
            
            // Calculate aggregate from daily stats
            const totalSessions = dailyStats.reduce((sum, day) => sum + day.totalSessions, 0);
            const totalSessionsWithEvents = dailyStats.reduce((sum, day) => sum + day.sessionsWithEvents, 0);
            const totalAddToCart = dailyStats.reduce((sum, day) => sum + day.sessionsWithAddToCart, 0);
            const totalOrders = dailyStats.reduce((sum, day) => sum + day.sessionsWithOrders, 0);
            
            const calculatedAggregate = {
                sessionStats: [{
                    totalSessions: totalSessions,
                    sessionsWithEvents: totalSessionsWithEvents.toString(),
                    sessionsWithoutEvents: (totalSessions - totalSessionsWithEvents).toString(),
                    sessionsWithAddToCart: totalAddToCart.toString(),
                    sessionsWithOrders: totalOrders.toString(),
                    uniqueCustomers: 0 // Not applicable for aggregated data
                }]
            };
            
            return {
                aggregate: calculatedAggregate,
                dailyStats,
                newCachedDays
            };
        } catch (error: any) {
            store.dispatch(setNotifications({ text: error.message, type: 'error' }));
            return rejectWithValue(error.message);
        }
    }
);

